import type { SeoFields } from '../types/seo'

/**
 * Vindbaarheid: wat Google en een deellink tonen, en wat er aan een titel of beschrijving
 * schort. Puur, zodat het beheer (controles en voorbeeld) en de webshop (de echte <title> en
 * meta) precies hetzelfde gebruiken.
 */

/** Google kort titels af rond deze lengte (± 580 px). */
export const SEO_TITLE_MAX = 60
/** Korter dan dit zegt in Google weinig. */
export const SEO_TITLE_MIN = 25
/** Google kort beschrijvingen af rond deze lengte. */
export const SEO_DESCRIPTION_MAX = 155
export const SEO_DESCRIPTION_MIN = 70

export type SeoResolved = { title: string; description: string; image: string }

export type SeoIssue = { level: 'error' | 'warning'; text: string }

const clean = (s: string | undefined) => (s ?? '').replace(/\s+/g, ' ').trim()

/** Eigen tekst als die er is, anders de automatische. Lege velden of alleen spaties tellen als leeg. */
export function effectiveSeo(seo: SeoFields | undefined, auto: SeoResolved): SeoResolved {
  return {
    title: clean(seo?.title) || auto.title,
    description: clean(seo?.description) || auto.description,
    image: clean(seo?.image) || auto.image,
  }
}

/** Leeg = undefined, zodat een leeggemaakt veld niet als "eigen tekst" blijft staan. */
export function cleanSeo(seo: SeoFields | undefined): SeoFields | undefined {
  const next: SeoFields = {}
  const title = clean(seo?.title)
  const description = clean(seo?.description)
  const image = clean(seo?.image)
  if (title) next.title = title
  if (description) next.description = description
  if (image) next.image = image
  return Object.keys(next).length ? next : undefined
}

/** Voor het vergelijken van titels: hoofdletters, leestekens en de merknaam erachter tellen niet. */
export const titleKey = (title: string, siteName = '') =>
  clean(siteName ? title.replace(new RegExp(`\\s*[|–-]\\s*${escapeRegExp(siteName)}\\s*$`, 'i'), '') : title)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

type CheckInput = {
  seo: SeoFields | undefined
  auto: SeoResolved
  /** Titels van de andere pagina's van hetzelfde soort (al met hun automatische terugval). */
  otherTitles?: string[]
  siteName?: string
  /** `false` = de foto heeft geen beschrijving; `undefined` = niet van toepassing. */
  hasImageAlt?: boolean
}

/** Wat schort er aan de vindbaarheid? Alleen oranje: niets hiervan houdt publiceren tegen. */
export function seoChecks({ seo, auto, otherTitles = [], siteName = '', hasImageAlt }: CheckInput): SeoIssue[] {
  const out: SeoIssue[] = []
  const { title, description } = effectiveSeo(seo, auto)

  if (title.length > SEO_TITLE_MAX) out.push({ level: 'warning', text: `Google-titel is lang (${title.length} tekens): na ± ${SEO_TITLE_MAX} valt de rest weg` })
  else if (title.length < SEO_TITLE_MIN) out.push({ level: 'warning', text: 'Google-titel is kort: noem ook waar het om gaat, bijv. merk of huidthema' })

  if (!description) out.push({ level: 'warning', text: 'Geen Google-beschrijving: Google kiest dan zelf een stukje tekst' })
  else if (description.length > SEO_DESCRIPTION_MAX)
    out.push({ level: 'warning', text: `Google-beschrijving is lang (${description.length} tekens): na ± ${SEO_DESCRIPTION_MAX} valt de rest weg` })
  else if (description.length < SEO_DESCRIPTION_MIN) out.push({ level: 'warning', text: 'Google-beschrijving is kort: één of twee zinnen werken het best' })

  const key = titleKey(title, siteName)
  if (key && otherTitles.some((t) => titleKey(t, siteName) === key))
    out.push({ level: 'warning', text: 'Een andere pagina heeft dezelfde Google-titel: maak hem uniek, anders kiest Google er één' })

  if (hasImageAlt === false) out.push({ level: 'warning', text: 'De foto heeft geen beschrijving (alt-tekst): voor Google Afbeeldingen en schermlezers' })
  return out
}
